import { Component, Inject, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { DataService } from '../../services/data.service';
import { FileUploadService } from '../../services/file-upload.service';
import { ChartProcessingService } from '../../services/chart-processing.service';
import { InteractiveChartComponent } from '../interactive-chart/interactive-chart.component';
import { ChartDataResponse } from '../../models/chart';

export interface VisualiseDialogData {
  columnName: string;
  columnInfo: any;
  availableColumns: string[];
  availableColumnsMetadata?: Array<{ field: string; dataType: string }>; // Column metadata for filtering
  dataSourceId?: string; // Optional - if provided, uses datasource endpoint
  uploadedData?: any; // Optional - if provided (and no dataSourceId), uses datapoints endpoint
  initialGraphType?: string; // Optional - initial graph type to display
  initialSecondColumn?: string; // Optional - initial second column for scatter/iqr
  hideYAxisSelector?: boolean; // Optional - hide Y-axis selector (e.g., from Anomaly Detection)
  hideXAxisSelector?: boolean; // Optional - hide X-axis selector (e.g., from Data Assessment)
  anomalyIndices?: number[]; // Optional - indices of anomalous points for highlighting
}

@Component({
  selector: 'app-visualise-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatTableModule,
    InteractiveChartComponent
  ],
  templateUrl: './visualise-dialog.component.html',
  styleUrls: ['./visualise-dialog.component.scss']
})
export class VisualiseDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('chartContainer', { read: ElementRef }) chartContainer?: ElementRef;
  @ViewChild(InteractiveChartComponent) interactiveChart?: InteractiveChartComponent;
  
  selectedGraphType: string = 'hist';
  selectedSecondColumn: string = '';
  selectedXColumn: string = ''; // X-axis column for scatter plots
  chartData?: ChartDataResponse;
  isLoading: boolean = false;
  errorMessage: string = '';
  availableGraphTypes: Array<{ value: string; label: string }> = [];
  selectedData: any[] = []; // Store selected data for export
  displayedColumns: string[] = []; // Columns for export
  
  constructor(
    public dialogRef: MatDialogRef<VisualiseDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: VisualiseDialogData,
    private dataService: DataService,
    private fileUploadService: FileUploadService,
    private chartProcessingService: ChartProcessingService
  ) {}

  ngOnInit(): void {
    // Compute available graph types once based on column data type
    this.availableGraphTypes = this.computeAvailableGraphTypes();
    
    // Use initial graph type if provided, otherwise default to 'hist'
    if (this.data.initialGraphType && this.availableGraphTypes.some(t => t.value === this.data.initialGraphType)) {
      this.selectedGraphType = this.data.initialGraphType;
    } else if (this.availableGraphTypes.length > 0 && !this.availableGraphTypes.some(t => t.value === this.selectedGraphType)) {
      this.selectedGraphType = this.availableGraphTypes[0].value;
    }
    
    // Initialize columns for scatter plot
    if (this.selectedGraphType === 'scatter') {
      if (this.data.initialSecondColumn) {
        // When opened from Anomaly Detection:
        // - initialSecondColumn is the selected X column from anomaly detection
        // - columnName is the anomaly Y column being analyzed
        this.selectedXColumn = this.data.initialSecondColumn;
        this.selectedSecondColumn = this.data.columnName;
      } else {
        // Default initialization:
        // - X-axis: first available column
        // - Y-axis: second available column (or first if only one exists)
        const availableColumns = this.getAvailableColumnsForScatter();
        if (availableColumns.length > 0) {
          this.selectedXColumn = availableColumns[0];
          this.selectedSecondColumn = availableColumns.length > 1 ? availableColumns[1] : availableColumns[0];
        }
      }
    }
  }

  ngAfterViewInit(): void {
    // Load initial chart
    this.loadChart();
  }
  
  getChartTitle(): string {
    switch (this.selectedGraphType) {
      case 'hist':
        return 'Histogram Distribution';
      case 'kde':
        return 'KDE Distribution Graph';
      case 'bar':
        return 'Top 10 Distinct Values Bar Chart';
      case 'iqr':
        return this.selectedSecondColumn ? 
          `IQR by ${this.selectedSecondColumn}` : 
          `${this.data.columnName} IQR`;
      case 'scatter':
        return `Scatter Plot: ${this.selectedXColumn || this.data.columnName} vs ${this.selectedSecondColumn || 'Y Column'}`;
      default:
        return 'Chart';
    }
  }
  
  onCancel(): void {
    this.dialogRef.close();
  }
  
  onGraphTypeChange(): void {
    // Handle graph type specific logic
    if (this.selectedGraphType === 'scatter') {
      // Scatter requires both X and Y columns
      if (!this.selectedXColumn) {
        this.selectedXColumn = this.data.columnName;
      }
      if (!this.selectedSecondColumn) {
        const availableColumns = this.getAvailableColumnsForScatter();
        this.selectedSecondColumn = availableColumns.length > 0 ? availableColumns[0] : '';
      }
    } else if (this.selectedGraphType === 'iqr') {
      // IQR allows no second column (optional)
      // Keep current selection or clear it
    } else {
      // Other graph types don't need second column
      this.selectedSecondColumn = '';
      this.selectedXColumn = '';
    }
    
    console.log('Graph type changed to:', this.selectedGraphType);
    this.loadChart(); // Load new chart from API
  }
  
  onSecondColumnChange(): void {
    console.log('Second column changed to:', this.selectedSecondColumn);
    this.loadChart(); // Reload chart with new column
  }

  loadChart(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.chartData = undefined;

    const chartType = this.selectedGraphType;
    this.loadInteractiveChart(chartType);
  }

  /**
   * Load interactive chart by fetching raw data and processing locally
   */
  private loadInteractiveChart(chartType: string): void {
    // Determine which columns we need
    let primaryColumn = this.data.columnName;
    let secondaryColumn = this.selectedSecondColumn;
    
    // For scatter plots, use selectedXColumn and selectedSecondColumn
    if (chartType === 'scatter') {
      primaryColumn = this.selectedXColumn || this.data.columnName;
      secondaryColumn = this.selectedSecondColumn;
    }
    
    const columns = [primaryColumn];
    if (secondaryColumn) {
      columns.push(secondaryColumn);
    }

    if (this.data.dataSourceId) {
      // FOR DATASOURCE: Fetch specific columns from the new endpoint
      this.dataService.getDataSourceColumns(this.data.dataSourceId, columns).subscribe({
        next: (rawData) => {
          console.log('Raw data from datasource:', rawData);
          this.processAndDisplayChart(chartType, rawData);
        },
        error: (error) => {
          console.error('Error loading data from datasource:', error);
          this.errorMessage = 'Failed to load chart data. Please try again.';
          this.isLoading = false;
        }
      });
    } else {
      // FOR UPLOADED FILES: Extract data from memory
      let uploadedData = this.data.uploadedData;
      
      if (!uploadedData) {
        const fileData = this.fileUploadService.getUploadedData();
        if (!fileData) {
          this.errorMessage = 'No data available. Please upload data first.';
          this.isLoading = false;
          return;
        }
        uploadedData = fileData.data;
      }

      if (!Array.isArray(uploadedData)) {
        this.errorMessage = 'Invalid data format. Expected an array of data points.';
        this.isLoading = false;
        return;
      }

      console.log('Using uploaded data:', uploadedData);
      this.processAndDisplayChart(chartType, uploadedData);
    }
  }

  /**
   * Process raw data and create chart data structure
   */
  private processAndDisplayChart(chartType: string, rawData: any[]): void {
    try {
      // Determine which columns to use
      let primaryColumn = this.data.columnName;
      let secondaryColumn = this.selectedSecondColumn;
      
      // For scatter plots, use selectedXColumn as primary
      if (chartType === 'scatter') {
        primaryColumn = this.selectedXColumn || this.data.columnName;
        secondaryColumn = this.selectedSecondColumn;
      }
      
      // Process the raw data into chart-ready format
      const processedData = this.chartProcessingService.processChartData(
        chartType,
        rawData,
        primaryColumn,
        secondaryColumn
      );

      console.log('Processed chart data:', processedData);

      // Create ChartDataResponse with the processed data
      this.chartData = {
        data: processedData,
        image: undefined // No image for interactive mode
      };

      this.isLoading = false;
    } catch (error) {
      console.error('Error processing chart data:', error);
      this.errorMessage = error instanceof Error ? error.message : 'Failed to process chart data';
      this.isLoading = false;
    }
  }



  onSelectionChange(selectedData: any[]): void {
    console.log('Selected data:', selectedData);
    this.selectedData = selectedData;
    
    // Update displayed columns
    if (selectedData.length > 0) {
      this.displayedColumns = Object.keys(selectedData[0]);
    } else {
      this.displayedColumns = [];
    }
  }

  clearSelection(): void {
    // Clear the dialog's selected data
    this.selectedData = [];
    this.displayedColumns = [];
    
    // Clear the selection on the interactive chart component
    if (this.interactiveChart) {
      this.interactiveChart.clearSelection();
    }
  }
  
  onXColumnChange(): void {
    console.log('X column changed to:', this.selectedXColumn);
    this.loadChart(); // Reload chart with new X column
  }
  
  onExport(): void {
    if (this.selectedData.length === 0) return;

    // Convert to CSV
    const headers = this.displayedColumns.join(',');
    const rows = this.selectedData.map(row => 
      this.displayedColumns.map(col => {
        const value = row[col];
        // Escape values with commas or quotes
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',')
    );
    
    const csv = [headers, ...rows].join('\n');
    
    // Download CSV
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `selected_data_${new Date().getTime()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  getSecondColumnPlaceholder(): string {
    if (this.selectedGraphType === 'scatter') {
      return 'Select Y-axis column (required)';
    } else if (this.selectedGraphType === 'iqr') {
      return 'Select grouping column (optional)';
    }
    return 'Select second column';
  }
  
  getXColumnPlaceholder(): string {
    return 'Select X-axis column (required)';
  }

  requiresSecondColumn(): boolean {
    // Only scatter plot requires a second column on X-axis
    return this.selectedGraphType === 'scatter';
  }
  
  getAvailableColumns(): string[] {
    // Return all columns except the primary selected column
    return this.data.availableColumns.filter(col => col !== this.data.columnName);
  }
  
  /**
   * Get available columns for scatter plot axes (numeric/date only, excluding currently selected)
   */
  getAvailableColumnsForScatter(): string[] {
    let columns = this.data.availableColumns;
    
    // Filter out string columns (only allow numeric/date columns)
    if (this.data.availableColumnsMetadata) {
      columns = columns.filter(col => {
        const metadata = this.data.availableColumnsMetadata!.find(m => m.field === col);
        return metadata && !this.isStringType(metadata.dataType);
      });
    }
    
    return columns;
  }
  
  /**
   * Get available X-axis columns for scatter (exclude current Y-axis selection)
   */
  getAvailableXColumns(): string[] {
    return this.getAvailableColumnsForScatter().filter(col => col !== this.selectedSecondColumn);
  }
  
  /**
   * Get available Y-axis columns for scatter (exclude current X-axis selection)
   */
  getAvailableYColumns(): string[] {
    return this.getAvailableColumnsForScatter().filter(col => col !== this.selectedXColumn);
  }

  /**
   * Check if the column data type is a string/text type
   */
  private isStringType(dataType?: string): boolean {
    if (!dataType) return false;
    const normalizedType = dataType.toLowerCase();
    return ['string', 'str', 'text', 'varchar', 'char', 'nvarchar', 'nchar', 'object'].includes(normalizedType);
  }

  /**
   * Determine the Y-axis label based on chart type.
   * For chart types that don't use a second column, returns a descriptive label.
   * For chart types that do use a second column, returns the selected second column name.
   */
  private getYLabel(): string {
    if (this.selectedSecondColumn) {
      return this.selectedSecondColumn;
    }
    switch (this.selectedGraphType) {
      case 'hist': return 'Frequency';
      case 'kde':  return 'Density';
      case 'bar':  return 'Count';
      case 'iqr':  return 'Value';
      default:     return 'Value';
    }
  }

  /**
   * Compute available graph types based on the column's data type
   * This should be called once during initialization, not in the template
   */
  private computeAvailableGraphTypes(): Array<{ value: string; label: string }> {
    const columnType = this.data.columnInfo?.dataType;
    
    // For string columns, only show Histogram and Top 10 Distinct
    if (this.isStringType(columnType)) {
      return [
        { value: 'hist', label: 'Histogram Distribution' },
        { value: 'bar', label: 'Top 10 Distinct Values Bar Chart' }
      ];
    }
    
    // For all other types (numeric, date, etc.), show all options
    return [
      { value: 'hist', label: 'Histogram Distribution' },
      { value: 'kde', label: 'KDE Distribution Graph' },
      { value: 'bar', label: 'Top 10 Distinct Values Bar Chart' },
      { value: 'iqr', label: 'IQR by Optional Second Column' },
      { value: 'scatter', label: 'Scatter by Mandatory Second Column' }
    ];
  }
}
